import React, { ReactElement } from "react";
import { Box, IconButton, IconButtonProps, Stack } from "@mui/material";
import { HashLink as Link } from "react-router-hash-link";
import MuiLink from "@mui/material/Link";
import { Accessible, SvgIconComponent } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { actionButtonStyle } from "./actionButtonStyle";

export type ActionButtonProps = {
  icon?: SvgIconComponent;
  to?: string;
  onClick?: () => void;
  isHidden?: boolean;
  divider?: boolean;
  text: string;
  useCondensedView?: boolean;
  pulsating?: boolean;
  href?: string;
  tooltip?: string;
};

const ActionButton = ({
  icon: Icon = Accessible,
  text,
  to,
  href,
  onClick = () => {},
  isHidden = false,
  useCondensedView,
  divider = false,
  pulsating = false,
  tooltip,
  ...rest
}: ActionButtonProps) => {
  if (isHidden) {
    return null;
  }

  let iconButtonProps: IconButtonProps<React.ElementType> = {
    onClick: onClick,
  };
  if (to) {
    iconButtonProps.component = Link;
    iconButtonProps.to = to;
  }
  if (href) {
    iconButtonProps.component = MuiLink;
    iconButtonProps.href = href;
    iconButtonProps.target = "_blank";
    iconButtonProps.rel = "noreferrer";
    iconButtonProps.underline = "none";
  }

  const InternalTooltip = ({ children }: { children: ReactElement }) => {
    const tooltipLabel = tooltip ?? (useCondensedView ? text : null);
    if (!tooltipLabel) {
      return <>{children}</>;
    }
    return (
      <Tooltip title={tooltipLabel} placement={"left"}>
        {children}
      </Tooltip>
    );
  };

  return (
    <Stack
      {...rest}
      className={"actionButton"}
      sx={{
        alignItems: "center",
      }}
    >
      <IconButton {...iconButtonProps} size="large" sx={pulsating ? actionButtonStyle.pulse : {}}>
        <InternalTooltip>
          <Icon fontSize={useCondensedView ? "medium" : "large"} />
        </InternalTooltip>
      </IconButton>
      {!useCondensedView && <Box sx={actionButtonStyle.actionTitle}>{text}</Box>}
      {divider && <Box sx={actionButtonStyle.actionButtonDivider} />}
    </Stack>
  );
};

export default ActionButton;
